import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { useFieldAuth } from './FieldAuthContext';
import { apiRequest } from '../services/api';

interface SubscriptionContextType {
    daysRemaining: number | null;
    subscriptionPlan: string | null;
    isActive: boolean;
    loading: boolean;
    isFirstLogin: boolean;
    pendingPlan: string | null;
    pendingPlanExpiresAt: string | null;
    checkSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType>({
    daysRemaining: null,
    subscriptionPlan: null,
    isActive: false,
    loading: false,
    isFirstLogin: false,
    pendingPlan: null,
    pendingPlanExpiresAt: null,
    checkSubscription: async () => { },
});

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user: mainUser, isAuthenticated: isMainAuthenticated } = useAuth();
    const { user: fieldUser, isAuthenticated: isFieldAuthenticated } = useFieldAuth();

    const isAuthenticated = isMainAuthenticated || isFieldAuthenticated;
    const user = mainUser || fieldUser;
    const organizationId = mainUser?.organizationId || fieldUser?.organizationId;

    const [daysRemaining, setDaysRemaining] = useState<number | null>(null);
    const [subscriptionPlan, setSubscriptionPlan] = useState<string | null>(null);
    const [isActive, setIsActive] = useState<boolean>(true); // Default to true to prevent flash
    const [loading, setLoading] = useState<boolean>(false);
    const [alertShown, setAlertShown] = useState<boolean>(false);
    const [isFirstLogin, setIsFirstLogin] = useState<boolean>(false);
    const [pendingPlan, setPendingPlan] = useState<string | null>(null);
    const [pendingPlanExpiresAt, setPendingPlanExpiresAt] = useState<string | null>(null);

    // Reset state when user logs out
    useEffect(() => {
        if (!isAuthenticated) {
            setDaysRemaining(null);
            setSubscriptionPlan(null);
            setIsActive(true); // Reset to true for next login
            setAlertShown(false);
            setIsFirstLogin(false);
            setPendingPlan(null);
            setPendingPlanExpiresAt(null);
        }
    }, [isAuthenticated]);

    const checkSubscription = async (retryCount = 0) => {
        if (!isAuthenticated) return;

        try {
            // Only set loading on first try to avoid UI flicker
            if (retryCount === 0) setLoading(true);

            console.log(`[SubscriptionContext] Fetching subscription status... (Attempt ${retryCount + 1})`);

            const data = await apiRequest('/subscription/status', 'GET');

            // If we get null values for critical data, throw error to trigger retry
            if (!data || data.daysRemaining === null || data.subscriptionPlan === null) {
                // Determine if we should retry
                if (retryCount < 2) { // Max 2 retries (3 total attempts)
                    console.warn(`[SubscriptionContext] Received incomplete data. Retrying... (${retryCount + 1}/2)`);
                    setTimeout(() => {
                        checkSubscription(retryCount + 1);
                    }, 1000 * (retryCount + 1)); // Exponential backoff: 1s, 2s
                    return;
                }
            }

            console.log('[SubscriptionContext] Received data:', {
                plan: data.plan,
                daysRemaining: data.daysRemaining,
                isActive: data.isActive,
                autoFixed: data.autoFixed
            });

            // If backend auto-fixed NULL data, log it
            if (data.autoFixed) {
                console.warn('[SubscriptionContext] Backend auto-fixed NULL subscription data. Refreshing in 1 second...');
                // Wait a moment and fetch again to ensure data is fresh
                setTimeout(async () => {
                    checkSubscription(retryCount + 1);
                }, 1000);
                return;
            }

            console.log('[SubscriptionContext] Setting state:', {
                daysRemaining: data.daysRemaining,
                plan: data.plan,
                isActive: data.isActive,
                isFirstLogin: data.isFirstLogin
            });

            setDaysRemaining(data.daysRemaining);
            setSubscriptionPlan(data.plan);
            setIsActive(data.isActive);
            setIsFirstLogin(data.isFirstLogin || false);
            setPendingPlan(data.pendingPlan || null);
            setPendingPlanExpiresAt(data.pendingPlanExpiresAt || null);

            // Debug logging for null values
            if (data.daysRemaining === null) {
                console.error('[SubscriptionContext] ⚠️ daysRemaining is NULL after retries!', {
                    plan: data.plan,
                    endDate: data.endDate,
                    isActive: data.isActive
                });
            } else {
                console.log('[SubscriptionContext] ✅ Subscription loaded successfully:', {
                    plan: data.plan,
                    daysRemaining: data.daysRemaining,
                    isActive: data.isActive
                });
            }

        } catch (error) {
            console.error('[SubscriptionContext] Subscription check failed:', error);

            // Retry on error if limit not reached
            if (retryCount < 2) {
                console.warn(`[SubscriptionContext] Error occurred. Retrying... (${retryCount + 1}/2)`);
                setTimeout(() => {
                    checkSubscription(retryCount + 1);
                }, 1000 * (retryCount + 1));
            }
        } finally {
            if (retryCount === 0 || retryCount >= 2) {
                setLoading(false);
            }
        }
    };


    useEffect(() => {
        if (isAuthenticated && organizationId) {
            checkSubscription();
        }
    }, [isAuthenticated, organizationId]);

    return (
        <SubscriptionContext.Provider value={{ 
            daysRemaining, subscriptionPlan, isActive, loading, isFirstLogin, 
            pendingPlan, pendingPlanExpiresAt, checkSubscription 
        }}>
            {children}
        </SubscriptionContext.Provider>
    );
};
